import { useTranslation } from "react-i18next";
import { TabView, TabPanel } from "primereact/tabview";
import { useNavigate, useOutletContext, useLocation } from "react-router";
import { useContext, useEffect, useState, useRef } from "react";
import Information from "./components/Information";
import Boundary from "./components/Boundary";
import Identification from "./components/Identification";
import ActivityData from "./components/ActivityData";
import EmissionsCalculation from "./components/EmissionsCalculation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import ImportanceIdentification from "./components/ImportanceIdentification";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import { Tooltip } from "primereact/tooltip"; // 新增 Tooltip 引入
import ImageLocaleModal from "../../components/ImageLocaleModal";
import { updateFeUsers2Info } from "../../utility/service/account-service";
import { AlertService } from "../../utility/service/alert-service";
import { AcctContext } from "../../utility/useReduce/userNameReduce";
import { tabPermissionUtils } from "../../utility/service/tab-permission-utils";
import { max } from "bignumber.js";

export default function Carbon_Detail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setAction,
    action,
    setItemObj,
    itemObj,
    setReportListObj,
    setTab1Action,
    flowStep,
    setFlowStep,
  } = useOutletContext();
  const [disabledTab1, setDisabledTab1] = useState(true);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [state, dispatchUser] = useContext(AcctContext); //訊息視窗
  const stepsContainerRef = useRef(null);
  // 流程圖模態框狀態
  const [flowCheck, setFlowCheck] = useState(false);
  const [flowModal, setFlowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  // 頁籤權限狀態
  const [tabPermissions, setTabPermissions] = useState(null);
  const [accessibleTabs, setAccessibleTabs] = useState([]);
  const [expanded, setExpanded] = useState(false);

  // 設置標記，表示從子頁面返回
  useEffect(() => {
    sessionStorage.setItem("fromCarbonChildPage", "true");
    if (activeIndex === -1 || flowStep === -1) {
      setFlowStep(0);
    }
  }, []);

  // 頁面進入時直接顯示流程圖
  useEffect(() => {
    // 檢查是否是從子頁面返回
    const isFromChildPage = sessionStorage.getItem("fromCarbonChildPage");
    const userWantsToSeeModal = state?.dontShowAgain === "N";
    if (!isFromChildPage) {
      setFlowModal(userWantsToSeeModal);
    }
    setFlowCheck(!userWantsToSeeModal);
  }, []);

  // 載入頁籤權限
  useEffect(() => {
    const loadTabPermissions = () => {
      try {
        // 從全域狀態獲取 F10103 的頁籤權限信息
        const tabPermissions = state?.tabPermissions?.["F10103"];

        if (!tabPermissions || tabPermissions.trim() === "") {
          // 如果沒有權限設定，設為空陣列（無任何權限）
          setAccessibleTabs([]);
          return;
        }

        // 解析頁籤權限字串
        const accessibleTabs = tabPermissions.split("|").filter((tab) => tab.trim() !== "");
        setAccessibleTabs(accessibleTabs);
      } catch (error) {
        // 如果載入失敗，設為空陣列（無任何權限）
        setAccessibleTabs([]);
      }
    };

    loadTabPermissions();
  }, [state?.tabPermissions]); // 依賴頁籤權限變化

  // 定義步驟配置
  const getSteps = () => {
    const allSteps = [
      { id: 0, title: t("inventory_information"), icon: "pi pi-info-circle", tabName: "inventory_information" },
      { id: 1, title: t("organizational_boundary"), icon: "pi pi-globe", tabName: "organizational_boundary" },
      {
        id: 2,
        title: t("emission_source_identification"),
        icon: "pi pi-search",
        tabName: "emission_source_identification",
      },
      { id: 3, title: t("activity_data"), icon: "pi pi-database", tabName: "activity_data" },
      { id: 4, title: t("Emissions_calculation"), icon: "pi pi-calculator", tabName: "emissions_calculation" },
      { id: 5, title: t("button.report"), icon: "pi pi-file-word", tabName: "report" },
    ];

    // 如果有重大性鑑別，插入到組織邊界之後
    if (itemObj?.importanceIdentification === "Y") {
      allSteps.splice(2, 0, {
        id: 2,
        title: t("importanceIdentification"),
        icon: "pi pi-star",
        tabName: "importanceIdentification",
      });
      // 重新調整後續步驟的 id
      for (let i = 3; i < allSteps.length; i++) {
        allSteps[i].id = i;
      }
    }

    // 重新調整步驟的 id
    allSteps.forEach((step, index) => {
      step.id = index;
    });

    return allSteps;
  };

  const steps = getSteps();

  // 檢查特定步驟是否有權限
  const hasStepPermission = (tabName) => {
    return accessibleTabs.includes(tabName);
  };

  // 檢查步驟是否可用
  const isStepEnabled = (stepIndex) => {
    if (stepIndex === 0) return true; // 第一個步驟總是可用
    if (action === "update" || action === "detail") return true; // 更新或查看模式所有步驟都可用

    // 新建報告書時，根據做過的步驟來判斷是否可以往回退不會被 disabled，並且在更新組織邊界後，後面的步驟全開
    if (action === "create" || action === "edit") {
      if (stepIndex >= 2 && maxIndex >= 2) {
        return true;
      }
      return stepIndex <= maxIndex;
    }

    // 創建模式：只有報告書步驟需要特殊處理
    if (stepIndex === steps.length - 1) {
      // 報告書步驟需要完成前幾個步驟才能點擊
      return stepIndex <= activeIndex + 1; // 允許提前一個步驟點擊報告書
    }

    return stepIndex <= activeIndex; // 其他步驟只能訪問已完成的步驟
  };

  // 檢查步驟是否完成
  const isStepCompleted = (stepIndex) => {
    // 報告書步驟特殊處理：當完成前幾個步驟後就顯示為已完成
    if (stepIndex === steps.length - 1) {
      return activeIndex >= steps.length - 2; // 完成倒數第二個步驟後，報告書就顯示為已完成
    }
    return stepIndex < activeIndex;
  };

  // 智慧縮放：只有在畫面寬度不足時才縮小前面步驟
  useEffect(() => {
    const container = stepsContainerRef.current;
    if (!container) return;

    const updateShrinkState = () => {
      // 1. 先全部移除 shrunk
      const stepBoxes = Array.from(container.querySelectorAll(".step-arrow-box"));
      stepBoxes.forEach((box) => box.classList.remove("shrunk"));

      // 2. 檢查是否需要縮小（畫面寬度嚴重不足時）
      if (container.scrollWidth > container.clientWidth + 50) {
        // 增加一些容錯空間
        // 畫面寬度嚴重不足，縮小前面的步驟
        for (let i = 0; i < activeIndex; i++) {
          if (stepBoxes[i]) {
            stepBoxes[i].classList.add("shrunk");
          }
        }
      }
      // 如果畫面寬度足夠或只是稍微不足，讓滾動條處理
    };

    // 初始更新
    updateShrinkState();

    // 監聽容器大小變化
    const resizeObserver = new ResizeObserver(() => {
      updateShrinkState();
    });
    resizeObserver.observe(container);

    // 清理函數
    return () => {
      resizeObserver.disconnect();
    };
  }, [activeIndex, steps.length]);

  // 判斷是否需要縮小：前面步驟且畫面寬度嚴重不足時
  const shouldShrink = (stepIndex) => {
    const container = stepsContainerRef.current;
    if (!container || stepIndex >= activeIndex) return false;

    // 檢查容器是否有嚴重的橫向滾動（增加容錯空間）
    return container.scrollWidth > container.clientWidth + 10;
  };

  // 處理步驟點擊（移除 alert，直接進入頁面）
  const handleStepClick = (stepIndex) => {
    const step = steps[stepIndex];
    if (!step) return;

    // 直接設定 activeIndex，讓頁面顯示權限提示內容
    if (isStepEnabled(stepIndex)) {
      // 如果是報告書步驟，檢查權限後決定是否跳轉
      if (stepIndex === steps.length - 1) {
        if (hasStepPermission(step.tabName)) {
          handleReportClick();
        } else {
          // 無權限時，直接設定 activeIndex 顯示權限提示
          setActiveIndex(stepIndex);
        }
      } else {
        setActiveIndex(stepIndex);
      }
    }
  };

  // 處理報告書點擊
  const handleReportClick = () => {
    // 設定報告書相關狀態
    setReportListObj([itemObj]);
    setAction("report");
    setTab1Action("report");

    // 跳轉到報告書頁面
    navigate("/management/carbonFootprint/report", {
      state: {
        companyName: itemObj?.companyName,
        activeIndex: activeIndex, // 保存當前步驟位置
      },
    });
  };

  // 計算當前流程步驟
  const getCurrentFlowStep = () => {
    if (!steps || steps.length === 0) {
      return 0;
    }

    const currentStep = steps[activeIndex];
    if (!currentStep) {
      return 0;
    }

    // 根據 tabName 來確定實際的流程步驟
    switch (currentStep.tabName) {
      case "inventory_information":
        return 0;
      case "organizational_boundary":
        return 1;
      case "importanceIdentification":
        return 2;
      case "emission_source_identification":
        return itemObj?.importanceIdentification === "Y" ? 3 : 2;
      case "activity_data":
        return itemObj?.importanceIdentification === "Y" ? 4 : 3;
      case "emissions_calculation":
        return itemObj?.importanceIdentification === "Y" ? 5 : 4;
      case "report":
        return itemObj?.importanceIdentification === "Y" ? 6 : 5;
      default:
        return 0;
    }
  };

  // 使用 useEffect 來更新 flowStep，避免在 render 期間 setState
  useEffect(() => {
    const newFlowStep = getCurrentFlowStep();
    if (flowStep !== newFlowStep) {
      setFlowStep(newFlowStep);
    }
    if (maxIndex < activeIndex) {
      setMaxIndex(activeIndex);
    }
  }, [activeIndex, itemObj?.importanceIdentification, flowStep]);

  // 渲染步驟內容
  const renderStepContent = () => {
    const currentStep = steps[activeIndex];

    if (!currentStep) {
      return <div className="text-center p-4"></div>;
    }

    const tabName = currentStep.tabName;

    // 檢查權限
    if (!hasStepPermission(tabName)) {
      return (
        <div className="text-center p-4">
          <h3>{t("no_permission_title")}</h3>
          <p>{t("no_permission_message")}</p>
        </div>
      );
    }

    switch (tabName) {
      case "inventory_information":
        return <Information setDisabledTab={setDisabledTab1} setActiveIndex={setActiveIndex} />;
      case "organizational_boundary":
        return <Boundary setActiveIndex={setActiveIndex} />;
      case "importanceIdentification":
        return <ImportanceIdentification setActiveIndex={setActiveIndex} />;
      case "emission_source_identification":
        return <Identification action={action} itemObj={itemObj} setActiveIndex={setActiveIndex} />;
      case "activity_data":
        return <ActivityData />;
      case "emissions_calculation":
        return <EmissionsCalculation />;
      case "report":
        return (
          <div className="text-center p-4">
            <h3>{t("button.report")}</h3>
            <p>{t("report.description") || "點擊上方報告書按鈕查看報告書"}</p>
          </div>
        );
      default:
        return <div className="text-center p-4"></div>;
    }
  };

  useEffect(() => {
    if (action === "") {
      navigate("/management/carbonFootprint");
    }
    if (action === "update" || action === "detail") {
      //更新的話 頁籤不disabled
      setDisabledTab1(false);
    }
  }, [action]);

  useEffect(() => {
    if (location?.state?.activeIndex != null) {
      setActiveIndex(location?.state?.activeIndex);
    }
  }, [location?.state?.activeIndex != null]);

  const copyToClipboard = (data) => {
    const textToCopy = data;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        handleSuccessMessage(dispatch, t("copy") + " " + t("message.success"));
      })
      .catch((error) => {});
  };

  // 處理文字截斷
  const truncateText = (text, maxLength = 7) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // 獲取顯示文字
  const getDisplayText = (text, stepIndex) => {
    // 只有在需要縮小時才截斷文字
    if (shouldShrink(stepIndex)) {
      // 縮小狀態下截斷文字
      const maxLength = 8;
      return truncateText(text, maxLength);
    }
    return text;
  };

  const updateUserInfo = (dontShow) => {
    const value = dontShow ? "Y" : "N";
    if (value !== state?.dontShowAgain) {
      updateFeUsers2Info({
        userDataList: [
          {
            refKey: "DONT_SHOW_AGAIN",
            refValue: value,
          },
        ],
      }).then((res) => {
        dispatchUser({ type: "DONT_SHOW_AGAIN", payload: { dontShowAgain: value } });
      });
    }
  };

  const placeNameField = (placeNames) => {
    if (!placeNames || placeNames.length === 0) return null;

    const showCount = 1; // 超過 1 個就收合
    const displayList = expanded ? placeNames : placeNames.slice(0, showCount);
    const remaining = placeNames.length - showCount;

    const displayValue = displayList.join(", ");

    return (
      <span className="inline-flex items-center gap-1">
        <span>{displayValue}</span>
        {remaining > 0 && (
          <Button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-button-icon-only"
            text
            size="small"
            icon={`${expanded ? "pi pi-angle-up" : "pi pi-angle-down"}`}
            style={{ height: "1.5rem", width: "0.2rem" }}
          />
        )}
      </span>
    );
  };

  return (
    <>
      <div className="flex justify-content-between flex-wrap align-items-center">
        <div className="align-items-start">
          <Button
            rounded
            text
            onClick={() => {
              navigate("/management/carbonFootprint");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
            {t("button.return_to_list")}
          </Button>
        </div>
        <div className="align-items-end">
          <label className="">
            {action === "create" ? t("button.create") : t("button.detail")}
            {itemObj?.planSn && (
              <Button
                type="button"
                style={{ alignItems: "flex-start", padding: "0px" }}
                size="small"
                tooltipOptions={{ position: "left" }}
                tooltip={`${t("button.copy")} ${itemObj?.planSn}`}
                text
                onClick={() => {
                  copyToClipboard(itemObj?.planSn);
                }}
                icon="pi pi-copy"
              />
            )}
          </label>
        </div>
      </div>

      {/* 上方廠商基本資訊(create 時不顯示) */}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <div className="grid align-items-center">
            <div className="col-10">
              <h3 className="m-0">
                {itemObj?.companyName} {action === "create" ? "" : itemObj?.year} {t("inventory_plan")}
              </h3>
            </div>
            <div className="col-2 flex justify-content-end">
              <Button
                tooltip={t("inventoryPlanFlow.title")}
                tooltipOptions={{ position: "top" }}
                rounded
                text
                severity="secondary"
                className="p-button-icon-only"
                onClick={() => {
                  setFlowModal(true);
                }}
              >
                <FontAwesomeIcon icon="fa-solid fa-compass" />
              </Button>
            </div>
            {action === "create" ? (
              ""
            ) : (
              <>
                <div className="col-12 md:col-4">
                  <label className="label-has-icon">{t("inventory_reason")}:</label>
                  <span>{itemObj?.displayCause}</span>
                </div>
                <div className="col-12 md:col-4">
                  <label className="label-has-icon">{t("standard_basis")}:</label>
                  <span>{itemObj?.displayCriterion}</span>
                </div>
                <div className="col-12 md:col-4">
                  <label className="label-has-icon">GWP:</label>
                  <span>{itemObj?.displayGwp}</span>
                </div>
                <div className="col-12 md:col-4">
                  <label className="label-has-icon">{t("place_name")}:</label>
                  {placeNameField(itemObj?.placeName)}
                </div>
                <div className="col-12 md:col-4">
                  <label className="label-has-icon">{t("planType")}:</label>
                  <span>{itemObj?.planTypeName}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 進度條樣式的分頁顯示 */}
      <div className="p-card">
        <div className="p-card-body">
          {/* 進度條 */}
          <div className="progress-steps mb-4">
            <div ref={stepsContainerRef} className="steps-container">
              {steps.length === 0 ? (
                <div className="no-permission-message text-center p-4 w-full">
                  <i className="pi pi-lock text-4xl text-muted mb-3"></i>
                  <h4 className="text-muted mb-2">{t("no_permission_title")}</h4>
                  <p className="text-muted">{t("no_permission_message")}</p>
                </div>
              ) : (
                steps.map((step, index) => {
                  const statusClass =
                    index === activeIndex
                      ? "active"
                      : index < activeIndex
                      ? "completed"
                      : isStepEnabled(index)
                      ? "enabled"
                      : "disabled";
                  const first = index === 0 ? "first" : "";
                  const last = index === steps.length - 1 ? "last" : "";
                  const shrinkClass = shouldShrink(index) ? "shrunk" : "";
                  const stepId = `step-tooltip-${index}`;
                  const displayText = getDisplayText(step.title, index);
                  const isTruncated = displayText !== step.title;
                  return (
                    <div
                      key={step.id}
                      id={stepId}
                      className={`step-arrow-box ${statusClass} ${first} ${last} ${shrinkClass}`}
                      onClick={() => handleStepClick(index)}
                    >
                      {displayText}
                      {/* 只在截斷時顯示 Tooltip */}
                      {isTruncated && <Tooltip target={`#${stepId}`} content={step.title} position="bottom" />}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* 步驟內容 */}
          <div className="step-content">{renderStepContent()}</div>
        </div>
      </div>

      {/* 流程圖模態框 */}
      <ImageLocaleModal
        visible={flowModal}
        checked={flowCheck}
        onHide={() => setFlowModal(false)}
        onDontShowAgain={(dontShow) => {
          updateUserInfo(dontShow);
        }}
        step={flowStep}
      />

      {/* 流程圖模態框 */}
      {/* <ImageLocaleModal
                visible={flowModal}
                checked={flowCheck}
                onHide={() => setFlowModal(false)}
                baseImagePath="/inventoryPlanFlow/inventoryPlanFlow"
                title={t('inventoryPlanFlow')}
                width="60%"
                onDontShowAgain={(dontShow) => {
                updateUserInfo(dontShow);
                }}
            /> */}
    </>
  );
}
